import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageSquare } from "lucide-react";

// Bug fix v1.4.1: Decode HTML entities from backend sanitization
const decodeHtmlEntities = (str) => {
  if (!str || typeof str !== 'string') return str;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
};

/**
 * Lightweight Markdown Renderer for Feedback
 * Supports: headers (###), bold (**), lists (- or *), inline code (`)
 */
const MarkdownRenderer = ({ content }) => {
  if (!content || typeof content !== 'string') return null;
  
  // First decode HTML entities
  const decoded = decodeHtmlEntities(content);
  
  // Split into lines and process
  const lines = decoded.split('\n');
  const elements = [];
  let listItems = [];
  let inList = false;
  
  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="space-y-3 my-4">
          {listItems}
        </ul>
      );
      listItems = [];
    }
    inList = false;
  };
  
  // Process inline formatting (bold, inline code)
  const processInline = (text) => {
    const parts = [];
    let remaining = text;
    let keyIndex = 0;
    
    // Process **bold** text
    const boldRegex = /\*\*([^*]+)\*\*/g;
    let lastIndex = 0;
    let match;
    
    while ((match = boldRegex.exec(remaining)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(remaining.slice(lastIndex, match.index));
      }
      // Add the bold text
      parts.push(
        <strong key={`bold-${keyIndex++}`} className="font-semibold text-foreground">
          {match[1]}
        </strong>
      );
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < remaining.length) {
      parts.push(remaining.slice(lastIndex));
    }
    
    return parts.length > 0 ? parts : remaining;
  };
  
  lines.forEach((line, index) => {
    const trimmed = line.trim();
    
    // Skip empty lines but flush lists
    if (!trimmed) {
      flushList();
      return;
    }
    
    // Headers (### or ##)
    if (trimmed.startsWith('###')) {
      flushList();
      const headerText = trimmed.replace(/^###\s*/, '');
      elements.push(
        <h3 key={`h3-${index}`} className="text-base font-bold text-foreground mt-4 mb-3 flex items-center gap-2">
          <span className="w-1 h-4 bg-primary rounded-full"></span>
          {processInline(headerText)}
        </h3>
      );
      return;
    }
    
    if (trimmed.startsWith('##')) {
      flushList();
      const headerText = trimmed.replace(/^##\s*/, '');
      elements.push(
        <h2 key={`h2-${index}`} className="text-lg font-bold text-foreground mt-4 mb-3">
          {processInline(headerText)}
        </h2>
      );
      return;
    }
    
    // List items (- or *)
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      inList = true;
      const itemText = trimmed.slice(2);
      
      // Check if it's a "Title: Description" pattern
      const colonMatch = itemText.match(/^(\*\*[^*]+\*\*:?)(.*)$/);
      
      if (colonMatch) {
        // Bold title with description
        listItems.push(
          <li key={`li-${index}`} className="flex gap-3 items-start">
            <span className="mt-2 w-2 h-2 rounded-full bg-primary/60 flex-shrink-0"></span>
            <div className="flex-1">
              <span className="font-semibold text-foreground">
                {colonMatch[1].replace(/\*\*/g, '')}
              </span>
              <span className="text-muted-foreground">
                {colonMatch[2]}
              </span>
            </div>
          </li>
        );
      } else {
        // Regular list item
        listItems.push(
          <li key={`li-${index}`} className="flex gap-3 items-start">
            <span className="mt-2 w-2 h-2 rounded-full bg-primary/60 flex-shrink-0"></span>
            <span className="text-muted-foreground leading-relaxed">
              {processInline(itemText)}
            </span>
          </li>
        );
      }
      return;
    }
    
    // Regular paragraph
    flushList();
    elements.push(
      <p key={`p-${index}`} className="text-sm text-muted-foreground leading-relaxed my-2">
        {processInline(trimmed)}
      </p>
    );
  });
  
  // Flush any remaining list items
  flushList();
  
  return <div className="space-y-1">{elements}</div>;
};

export const FeedbackPanel = ({ feedback }) => {
  if (!feedback) return null;

  // Handle both string and object feedback
  const feedbackText = typeof feedback === 'string' 
    ? feedback 
    : JSON.stringify(feedback, null, 2);

  return (
    <Card className="border border-border shadow-md overflow-hidden">
      <CardHeader className="pb-3 bg-gradient-to-r from-primary/5 to-transparent">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          Feedback
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <MarkdownRenderer content={feedbackText} />
      </CardContent>
    </Card>
  );
};

export default FeedbackPanel;
